import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ShoppingBag, 
  ShoppingCart, 
  Star, 
  ShieldCheck, 
  Truck, 
  RotateCcw, 
  ZoomIn, 
  Play, 
  MessageSquare
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart'; // Import useCart để đồng bộ giỏ hàng
import apiService from '../../components/services/apiService';
import { formatVND } from '../../utils/formatters';

export default function ProductDetailPage() {
  const { id } = useParams(); // Lấy ID sản phẩm từ URL
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { addToCart } = useCart(); // Lấy hàm addToCart từ CartContext

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // State quản lý Hình ảnh hiển thị chính & Zoom
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  // State quản lý Biến thể (Variants) & Số lượng
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentVariant, setCurrentVariant] = useState(null);

  // State quản lý Tab thông tin chi tiết
  const [activeTab, setActiveTab] = useState('specs'); // 'specs' | 'description' | 'guide' | 'policy'

  // State quản lý Review đơn giản
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  // Tự động tìm biến thể khớp khi người dùng đổi Màu hoặc Kích thước
  useEffect(() => {
    if (product && product.variants) {
      const matched = product.variants.find(v => {
        const matchColor = !selectedColor || v.color === selectedColor;
        const matchSize = !selectedSize || v.size === selectedSize;
        return matchColor && matchSize;
      });
      setCurrentVariant(matched || product.variants[0]);
    }
  }, [selectedColor, selectedSize, product]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      // Gọi API lấy chi tiết sản phẩm theo đúng tiền tố /home
      const res = await apiService.get(`/home/products/${id}`);
      const productData = res.data?.data || res.data;
      
      setProduct(productData);

      // Thiết lập giá trị mặc định ban đầu cho biến thể đầu tiên nếu có
      if (productData.variants && productData.variants.length > 0) {
        const firstVar = productData.variants[0];
        setSelectedColor(firstVar.color || '');
        setSelectedSize(firstVar.size || '');
        setCurrentVariant(firstVar);
      }

      // Giả lập tải danh sách đánh giá
      setReviews(productData.reviews || [
        { id: 1, userName: 'Nguyễn Văn A', rating: 5, comment: 'Sản phẩm chuẩn nội địa Nhật, đóng gói cực kỳ cẩn thận!', createdAt: '2026-10-10' },
        { id: 2, userName: 'Trần Thị B', rating: 4.5, comment: 'Chất lượng tuyệt vời, dùng rất thích.', createdAt: '2026-10-12' }
      ]);

    } catch (error) {
      console.error("Lỗi tải chi tiết sản phẩm:", error);
      showToast("Không thể tải thông tin sản phẩm", "error");
    } finally {
      setLoading(false);
    }
  };

  // Thêm vào giỏ hàng (Đồng bộ cả Backend API và Frontend CartContext)
  const handleAddToCart = async (showNotification = true) => {
    if (!product) return false;

    try {
      // 1. Gọi API Backend lưu vào DB nếu đã đăng nhập
      if (user) {
        await apiService.post('/cart/add', {
          productId: product.id || product.realId,
          variantId: currentVariant?.id,
          quantity: quantity,
          color: selectedColor,
          size: selectedSize
        });
      }

      // 2. Đẩy vào CartContext ở Frontend để CartDrawer hiển thị ngay lập tức
      const cartItem = {
        ...product,
        id: product.id || product.realId,
        variantId: currentVariant?.id,
        price: currentVariant?.price || product.price,
        selectedColor: selectedColor,
        selectedSize: selectedSize,
        stock: currentVariant?.stockQuantity || product.stock || 50
      };

      if (addToCart) {
        addToCart(cartItem, quantity);
      }

      if (showNotification) {
       // showToast("Đã thêm sản phẩm vào giỏ hàng thành công! 🎉", "success");
      }
      return true;

    } catch (error) {
      console.error("Lỗi thêm giỏ hàng:", error);
      showToast("Không thể thêm vào giỏ hàng lúc này", "error");
      return false;
    }
  };

  // Mua ngay (Thêm vào giỏ xong chuyển thẳng tới trang checkout/thanh toán)
  const handleBuyNow = async () => {
    const success = await handleAddToCart(false); // Thêm giỏ ngầm không hiện toast trùng lặp
    if (success) {
      if (!user) {
        showToast("Vui lòng đăng nhập để tiếp tục thanh toán", "warning");
        return;
      }
      navigate('/checkout'); // Chuyển hướng đến trang thanh toán
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-24 text-center text-slate-400 text-xs font-semibold">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        Đang tải thông tin chi tiết sản phẩm...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="text-xl font-extrabold text-slate-900">Không tìm thấy sản phẩm</h2>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold transition shadow-md cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay về trang chủ
        </button>
      </div>
    );
  }

  const mediaList = product.media || [
    { type: 'image', url: product.thumbnail || product.image }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {/* Nút quay lại */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white hover:bg-slate-50 border border-slate-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay lại
        </button>
      </div>

      {/* --- HERO SECTION: ẢNH & BIẾN THỂ --- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
        
        {/* Cột Trái: Media Gallery */}
        <div className="lg:col-span-6 space-y-4">
          <div 
            className="relative w-full h-[380px] bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 flex items-center justify-center cursor-zoom-in group"
            onClick={() => setIsZoomed(!isZoomed)}
          >
            {mediaList[activeMediaIndex]?.type === 'video' ? (
              <div className="relative w-full h-full flex items-center justify-center bg-black">
                <video src={mediaList[activeMediaIndex].url} controls className="w-full h-full object-contain" />
              </div>
            ) : (
              <img 
                src={mediaList[activeMediaIndex]?.url || product.thumbnail} 
                alt={product.name} 
                className={`w-full h-full object-cover transition duration-300 ${isZoomed ? 'scale-150 cursor-zoom-out' : 'group-hover:scale-105'}`} 
              />
            )}
            <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1">
              <ZoomIn className="w-3.5 h-3.5" /> Phóng to
            </div>
          </div>

          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {mediaList.map((media, index) => (
              <button
                key={index}
                onClick={() => setActiveMediaIndex(index)}
                className={`w-16 h-16 rounded-xl overflow-hidden border-2 flex-shrink-0 transition cursor-pointer relative ${
                  activeMediaIndex === index ? 'border-indigo-600 shadow-md' : 'border-slate-200 opacity-70 hover:opacity-100'
                }`}
              >
                <img src={media.url || product.thumbnail} alt="thumbnail" className="w-full h-full object-cover" />
                {media.type === 'video' && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white">
                    <Play className="w-5 h-5 fill-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Cột Phải: Thông tin & Mua hàng */}
        <div className="lg:col-span-6 space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div>
              <span className="bg-indigo-50 text-indigo-600 border border-indigo-200 px-3 py-1 rounded-full text-[11px] font-bold inline-block mb-2">
                {product.categoryName || 'Hàng Nội Địa Nhật'}
              </span>
              <h1 className="text-xl font-extrabold text-slate-900 leading-snug">
                {product.name}
              </h1>
              
              <div className="flex items-center gap-3 mt-2 text-xs">
                <div className="flex items-center gap-1 text-amber-500 font-bold">
                  <Star className="w-4 h-4 fill-amber-400" /> {product.ratingAverage || '4.9'}
                </div>
                <span className="text-slate-300">|</span>
                <span className="text-slate-500">Đã bán {product.soldCount || '128'}</span>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-baseline gap-3">
              <span className="text-2xl font-extrabold text-indigo-600">
                {formatVND(currentVariant?.price || product.price)}
              </span>
              {product.originalPrice && (
                <span className="text-xs text-slate-400 line-through">
                  {formatVND(product.originalPrice)}
                </span>
              )}
            </div>

            {/* Màu sắc */}
            {product.colors && product.colors.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 block">
                  Màu sắc: <span className="text-indigo-600 font-extrabold">{selectedColor}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold border transition cursor-pointer ${
                        selectedColor === color 
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Kích thước */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 block">
                  Kích thước / Dung lượng: <span className="text-indigo-600 font-extrabold">{selectedSize}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold border transition cursor-pointer ${
                        selectedSize === size 
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Số lượng */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 block">Số lượng</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 text-slate-600 hover:bg-slate-100 font-bold transition cursor-pointer"
                  >
                    -
                  </button>
                  <span className="w-10 text-center text-xs font-extrabold text-slate-800">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 text-slate-600 hover:bg-slate-100 font-bold transition cursor-pointer"
                  >
                    +
                  </button>
                </div>
                <span className="text-[11px] text-slate-400">
                  Còn lại: <strong className="text-slate-700">{currentVariant?.stockQuantity || product.stock || 50}</strong> sản phẩm
                </span>
              </div>
            </div>
          </div>

          {/* Nút Hành động */}
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={() => handleAddToCart(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 font-bold rounded-2xl text-xs transition cursor-pointer shadow-sm"
            >
              <ShoppingCart className="w-4 h-4" /> Thêm giỏ hàng
            </button>
            <button
              onClick={handleBuyNow}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl text-xs transition cursor-pointer shadow-md"
            >
              <ShoppingBag className="w-4 h-4" /> Mua ngay
            </button>
          </div>

        </div>
      </div>

      {/* --- THÔNG TIN CHI TIẾT & TABS --- */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-100">
          {[
            { key: 'specs', label: 'Thông số kỹ thuật' },
            { key: 'description', label: 'Mô tả sản phẩm' },
            { key: 'guide', label: 'Hướng dẫn sử dụng' },
            { key: 'policy', label: 'Chính sách bảo hành' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="text-xs text-slate-600 leading-relaxed space-y-4">
          {activeTab === 'specs' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Thương hiệu', value: product.brand || 'Chính hãng Nhật Bản' },
                { label: 'Xuất xứ', value: product.origin || 'Nhật Bản' },
                { label: 'Chất liệu', value: product.material || 'Cao cấp an toàn' },
                { label: 'Trọng lượng', value: product.weight || '300g' },
              ].map((spec, idx) => (
                <div key={idx} className="flex justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 font-bold">{spec.label}</span>
                  <span className="text-slate-800 font-semibold">{spec.value}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'description' && (
            <div className="prose prose-sm max-w-none text-slate-700 space-y-3">
              <p>{product.description || 'Sản phẩm chất lượng cao được nhập khẩu trực tiếp từ thị trường nội địa Nhật Bản.'}</p>
            </div>
          )}

          {activeTab === 'guide' && (
            <div className="space-y-2">
              <p className="font-bold text-slate-800">Lưu ý khi sử dụng và bảo quản:</p>
              <ul className="list-disc pl-5 space-y-1 text-slate-600">
                <li>Bảo quản nơi khô ráo, thoáng mát, tránh ánh nắng trực tiếp.</li>
                <li>Tuân thủ đúng hướng dẫn kèm theo bao bì sản phẩm.</li>
              </ul>
            </div>
          )}

          {activeTab === 'policy' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                <ShieldCheck className="w-5 h-5 text-indigo-600 mb-1" />
                <h4 className="font-bold text-slate-800">Bảo hành chính hãng</h4>
                <p className="text-[11px] text-slate-400">Cam kết bảo hành theo chính sách nhà sản xuất.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                <RotateCcw className="w-5 h-5 text-indigo-600 mb-1" />
                <h4 className="font-bold text-slate-800">Đổi trả 7 ngày</h4>
                <p className="text-[11px] text-slate-400">Hỗ trợ đổi trả dễ dàng nếu có lỗi từ nhà sản xuất.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                <Truck className="w-5 h-5 text-indigo-600 mb-1" />
                <h4 className="font-bold text-slate-800">Vận chuyển nhanh</h4>
                <p className="text-[11px] text-slate-400">Giao hàng toàn quốc nhanh chóng, an toàn.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- ĐÁNH GIÁ & BÌNH LUẬN --- */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" /> Đánh giá & Bình luận sản phẩm
          </h3>
          <span className="text-xs font-bold text-indigo-600">{reviews.length} đánh giá</span>
        </div>

        <div className="space-y-4">
          {reviews.map((rev) => (
            <div key={rev.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-800">{rev.userName}</span>
                <span className="text-[11px] text-slate-400">{rev.createdAt}</span>
              </div>
              <div className="flex items-center gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(rev.rating) ? 'fill-amber-400' : 'text-slate-300'}`} />
                ))}
              </div>
              <p className="text-xs text-slate-600">{rev.comment}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}